import React, { useEffect } from 'react';

import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import classNames from 'classnames';
import {
  faBold,
  faItalic,
  faLink,
  faListNumeric,
  faListUl,
  faUnderline,
} from '@fortawesome/pro-solid-svg-icons';

import { Button } from 'components/ui/button';

interface Props {
  value: string;
  onChange: (html: string) => void;
}

export default function EmailTextEditor({ value, onChange }: Props) {
  const editor = useEditor({
    extensions: [StarterKit.configure({ link: { openOnClick: false } })],
    content: value,
    editorProps: {
      attributes: { class: 'email-text-editor-content p-3', style: 'min-height: 180px' },
    },
    onUpdate: ({ editor }) => onChange(editor.getHTML()),
  });

  useEffect(() => {
    if (editor && value !== editor.getHTML()) {
      editor.commands.setContent(value || '', { emitUpdate: false });
    }
  }, [value, editor]);

  if (!editor) return null;

  const setLink = () => {
    const previous = editor.getAttributes('link').href as string | undefined;
    const url = window.prompt('Enter a URL', previous || '');
    if (url === null) return;

    const chain = editor.chain().focus().extendMarkRange('link');
    if (url === '') {
      chain.unsetLink().run();
    } else {
      chain.setLink({ href: url }).run();
    }
  };

  const buttons = [
    { key: 'bold', icon: faBold, tooltip: 'Bold', run: () => editor.chain().focus().toggleBold().run() },
    { key: 'italic', icon: faItalic, tooltip: 'Italic', run: () => editor.chain().focus().toggleItalic().run() },
    {
      key: 'underline',
      icon: faUnderline,
      tooltip: 'Underline',
      run: () => editor.chain().focus().toggleUnderline().run(),
    },
    { key: 'link', icon: faLink, tooltip: 'Link', run: setLink },
    {
      key: 'bulletList',
      icon: faListUl,
      tooltip: 'Bullet List',
      run: () => editor.chain().focus().toggleBulletList().run(),
    },
    {
      key: 'orderedList',
      icon: faListNumeric,
      tooltip: 'Numbered List',
      run: () => editor.chain().focus().toggleOrderedList().run(),
    },
  ];

  return (
    <div className="border rounded overflow-hidden">
      <div className="d-flex gap-1 p-1 border-bottom">
        {buttons.map(button => (
          <Button
            className={classNames('squared p-1', editor.isActive(button.key) ? 'active text-body' : 'text-muted')}
            icon={button.icon}
            key={button.key}
            tooltip={button.tooltip}
            tooltipPlacement="top"
            variant="ghost"
            onClick={button.run}
          />
        ))}
      </div>
      <EditorContent editor={editor} />
    </div>
  );
}
